import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", key: "home" },
  { to: "/menu", key: "menu" },
  { to: "/galleria", key: "gallery" },
  { to: "/prenota", key: "reservation" },
  { to: "/dove-siamo", key: "location" },
  { to: "/da-vedere", key: "vicenza" },
  { to: "/lavora-con-noi", key: "careers" },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_modern-bistro-portal/artifacts/29uxid8m_PHOTO-2026-04-06-21-41-14.jpg";

const LANGS = [
  { code: "it", label: "IT", flag: "🇮🇹" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "at", label: "AT", flag: "🇦🇹" },
  { code: "fr", label: "FR", flag: "🇫🇷" },
  { code: "es", label: "ES", flag: "🇪🇸" },
  { code: "ar", label: "AR", flag: "🇸🇦" },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [siteImages, setSiteImages] = useState({});

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/site-images").then((r) => setSiteImages(r.data || {})).catch(() => {});
  }, []);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
  };

  const HEADER_BG_DEFAULT = "https://customer-assets.emergentagent.com/job_modern-bistro-portal/artifacts/eybg8wfd_B67AB930-114C-44B3-A785-FC3F8B9BA094.PNG";
  const headerBgRaw = siteImages["header.bg"];
  const headerBg = headerBgRaw
    ? (headerBgRaw.startsWith("http") ? headerBgRaw : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${headerBgRaw}`)
    : HEADER_BG_DEFAULT;

  const waPhone = (info?.whatsapp || "").replace(/[^0-9]/g, "");
  const waUrl = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent("Salve, vorrei avere informazioni su Harry's Salotto e Cucina.")}`
    : "https://wa.me/";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-white/10 overflow-hidden"
    >
      {/* Blurred background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${headerBg}')`,
          filter: "blur(14px) brightness(0.5) saturate(1.2)",
          transform: "scale(1.15)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/45" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none group">
          <span className="font-script text-4xl sm:text-5xl tracking-tight text-white -mb-0.5">
            Harry's
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/75 mt-1">
            Salotto &amp; Cucina
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide font-bold whitespace-nowrap ${isActive ? "text-[#E5C079]" : "text-white/95 hover:text-[#E5C079]"}`
              }
              end={n.to === "/"}
            >
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* WhatsApp icon button */}
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp"
            aria-label="WhatsApp"
            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#1FAE54] text-white transition-all hover:-translate-y-[1px]"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
              <path d="M20.52 3.48A11.88 11.88 0 0012.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.83 11.83 0 005.68 1.45h.01c6.54 0 11.84-5.3 11.84-11.83a11.78 11.78 0 00-3.37-8.48zm-8.47 18.22h-.01a9.84 9.84 0 01-5.02-1.38l-.36-.21-3.78.99 1.01-3.69-.24-.38a9.84 9.84 0 01-1.5-5.19c0-5.43 4.42-9.85 9.86-9.85 2.63 0 5.1 1.03 6.96 2.89a9.78 9.78 0 012.88 6.97c-.01 5.44-4.43 9.85-9.85 9.85zm5.4-7.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.16c-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.21 5.09 4.5.71.3 1.27.49 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z"/>
            </svg>
          </a>

          {/* Language switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className="flex items-center gap-2 px-3 py-2 text-sm tracking-wider uppercase text-white/85 hover:text-[#E5C079] transition-colors"
            >
              <span className="text-base leading-none" aria-hidden>
                {(LANGS.find((l) => l.code === i18n.language) || LANGS[0]).flag}
              </span>
              <span className="font-medium">{i18n.language.toUpperCase()}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px] bg-[#FAF9F6] border-[#E5E3D8]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => changeLang(l.code)}
                  className="cursor-pointer text-sm tracking-wider uppercase flex items-center gap-2"
                >
                  <span className="text-base leading-none" aria-hidden>{l.flag}</span>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/prenota"
            data-testid="header-book-btn"
            className="hidden sm:inline-block px-5 py-2.5 bg-[#8B3A3A] hover:bg-[#6C2A2A] text-white text-sm tracking-wide transition-all hover:-translate-y-[1px]"
          >
            {t("nav.reservation")}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="xl:hidden relative border-t border-white/10 bg-black/70 backdrop-blur-md">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `text-base font-serif font-bold text-2xl ${isActive ? "text-[#E5C079]" : "text-white"}`
                }
                end={n.to === "/"}
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
